import React from 'react'
import { forwardRef } from '@grupoboticario/flora-react-system'
import { FloraSwitchProps } from './types'
import {
  StyledSwitchInput,
  StyledSwitchWrapper,
  StyledSwitchLabel,
  StyledFloraSwitch,
  StyledCrossCircleIcon,
  StyledCheckCircleIcon,
} from './styles'
import { useFloraSwitch } from './useFloraSwitch'

/**
 * @component
 * @name Switch
 * @description Elemento gráfico interativo que possibilita ativar ou desativar uma função, alterando seu estado entre ligado e desligado, através de um simples toque ou clique. É utilizado em interfaces de aplicativos e sites para fornecer controle intuitivo sobre diferentes opções e configurações.
 * @tutorial https://florabook.gb.tech/v2/index.html?path=/docs/core-components-switch--default
 */
export const FloraSwitch = forwardRef<'input', FloraSwitchProps>(
  (
    {
      id,
      status = 'neutral',
      surfaceColor = 'light',
      label,
      disabled,
      'autotagging-flora': autotaggingFlora = 'off',
      'data-autotagging-flora-section': dataAutotaggingFloraSection = '',
      'data-flora-text': dataFloraText,
      'aria-label': ariaLabel,
      ...props
    },
    forwardedRef,
  ) => {
    const { ref, isChecked } = useFloraSwitch({ id, forwardedRef })
    return (
      <StyledSwitchWrapper htmlFor={id} disabled={Boolean(disabled)}>
        <StyledSwitchInput
          {...props}
          id={id}
          ref={ref}
          as="input"
          data-flora="switch"
          autotagging-flora={autotaggingFlora}
          data-flora-text={dataFloraText || ariaLabel || label}
          data-autotagging-flora-section={dataAutotaggingFloraSection}
          type="checkbox"
          surfaceColor={surfaceColor}
          status={status}
          disabled={disabled}
          aria-label={ariaLabel ?? label}
          aria-invalid={status === 'error'}
          aria-checked={isChecked}
          role="switch"
        />
        <StyledFloraSwitch>
          <>
            <StyledCheckCircleIcon
              data-icon="check-circle-icon"
              css={{ opacity: isChecked ? 1 : 0 }}
            />
            <StyledCrossCircleIcon
              data-icon="cross-circle-icon"
              css={{ opacity: !isChecked ? 1 : 0 }}
            />
          </>
        </StyledFloraSwitch>
        <StyledSwitchLabel aria-hidden surfaceColor={surfaceColor}>
          {label}
        </StyledSwitchLabel>
      </StyledSwitchWrapper>
    )
  },
)
